<template>
	<div class="xbd-table" ref="xbd-table">
		<div class="xbd-table__table" :style="maxHeight ? `max-height: ${maxHeight}px` : ''">
			<el-table
				ref="tableRef"
				:border="border"
				:stripe="stripe"
				:max-height="$attrs['max-height'] || maxHeight"
				v-bind="$attrs"
				v-on="$listeners"
			>
				<template
					v-for="(
						{ prop, label, required, headerSlot, slot, operates = [], showOverflowTooltip, ...rest }, key
					) in columns"
				>
					<el-table-column
						v-if="!('checked' in rest) || rest.checked"
						:key="rest.key || `${prop}-${key}`"
						:prop="prop"
						:label="label"
						v-bind="rest"
					>
						<template v-if="required" #header>
							<div>
								<span style="color: #f56c6c">*</span>
								{{ label }}
							</div>
						</template>

						<template v-else-if="headerSlot" #header>
							<slot
								:name="headerSlot"
								v-bind="{
									props: { prop, label, required, headerSlot, slot, operates, ...rest },
									index: key
								}"
							></slot>
						</template>

						<template v-if="slot" #default="scope">
							<slot v-bind="scope" :name="slot"></slot>
						</template>

						<template v-else-if="prop === 'operation'" #default="scope">
							<div class="operation">
								<template v-for="(operate, index) in operates">
									<template v-if="operate.slot">
										<slot :name="operate.slot" v-bind="scope" v-bind:operate="operate" v-bind:oIndex="index"></slot>
									</template>
									<template v-else>
										<el-button
											v-if="!returnResultWithKey('hide', operate, scope.row, scope.$index)"
											:type="operate.type || 'text'"
											:key="index"
											@click="operate.click(scope.row, scope.$index)"
											v-permission="operate.permission"
											:class="operate.customClass"
											:size="operate.size || 'mini'"
											v-bind="operate"
										>
											{{ operate.label }}
										</el-button>
									</template>
								</template>
							</div>
						</template>

						<template v-else-if="attributeIsFunction('render', rest)" #default="scope">
							<render-node :render="rest.render" :row="scope.row" :index="scope.$index" :row-key="prop" />
						</template>

						<template v-else-if="showOverflowTooltip" #default="{ row }">
							<fixed-text :text="$emptyTextConversion(row[prop])" />
						</template>

						<template v-else-if="!rest.type && !rest.formatter" #default="{ row }">
							{{ $emptyTextConversion(row[prop]) }}
						</template>

						<template v-if="'children' in rest && rest.children.length > 0">
							<child-nodes v-for="(column, childIndex) in rest.children" :key="childIndex" :column="column">
								<template v-for="(index, name) in $scopedSlots" #[name]="scope">
									<slot :name="name" v-bind="scope"></slot>
								</template>
							</child-nodes>
						</template>
					</el-table-column>
				</template>
			</el-table>
		</div>

		<div
			v-if="showPagination && pagination.total > (paginationConfig.pageSizes || [20, 50, 100])[0]"
			ref="pagination-dom"
			class="xbd-table__pagination"
		>
			<el-pagination
				:total="pagination.total"
				:page-size="pagination.limit || 20"
				:current-page="pagination.page"
				@current-change="currentChange"
				@size-change="sizeChange"
				:background="'background' in paginationConfig ? paginationConfig.background : true"
				:layout="paginationConfig.layout || 'sizes, total, prev, pager, next, jumper'"
				:hide-on-single-page="'hideOnSinglePage' in paginationConfig ? paginationConfig.hideOnSinglePage : false"
				:page-sizes="paginationConfig.pageSizes || [20, 50, 100]"
				v-bind="paginationConfig"
			/>
		</div>
	</div>
</template>

<script>
/**
 * 表格组件
 * @param {Array} columns 表格列配置
 * @param {String} columns[].prop 列属性
 * @param {String} columns[].label 列名称
 * @param {Boolean} columns[].required 是否必填
 * @param {String} columns[].headerSlot 表头插槽
 * @param {String} columns[].slot 插槽
 * @param {Array} columns[].render 渲染函数
 * @param {Array} columns[].operates 操作列配置
 * @param {String} columns[].operates[].label 操作名称
 * @param {String} columns[].operates[].type 操作类型
 * @param {String} columns[].operates[].permission 操作权限
 * @param {String} columns[].operates[].customClass 操作自定义类名
 * @param {String} columns[].operates[].size 操作按钮大小
 * @param {Function} columns[].operates[].click 操作点击事件
 * @param {Boolean} columns[].operates[].hide 是否隐藏操作
 * @param {any} columns[].operates[]... 其他element-ui el-button属性
 * @param {any} columns[].... 其他element-ui el-table-column属性
 * @param {Boolean} stripe 是否显示斑马纹
 * @param {Boolean} border 是否显示边框
 * @param {Boolean} flex 是否自适应高度
 * @param {Boolean} showPagination 是否显示分页
 * @param {Object} pagination 分页配置
 *
 * @event current-change 当前页码改变时触发
 * @event size-change 每页显示数量改变时触发
 * @event paginationChange 分页改变时触发
 */
import ChildNodes from "./ChildNodes.vue";
import RenderNode from "./RenderNode.vue";

export default {
	name: "XbdTable",
	inheritAttrs: false,
	components: {
		ChildNodes,
		RenderNode
	},
	props: {
		columns: {
			type: Array,
			required: true,
			default: () => []
		},
		stripe: {
			type: Boolean,
			default: true
		},
		border: {
			type: Boolean,
			default: true
		},
		showPagination: {
			type: Boolean,
			default: true
		},
		pagination: {
			type: Object,
			default: () => ({
				total: 0,
				limit: 20,
				page: 1
			})
		},
		paginationConfig: {
			type: Object,
			default: () => ({
				// 是否为分页按钮添加背景色
				background: true,
				// 分页布局
				layout: "sizes, total, prev, pager, next, jumper",
				// 只有一页时隐藏分页
				hideOnSinglePage: false,
				// 每页显示数量
				pageSizes: [20, 50, 100]
			})
		},
		flex: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			maxHeight: "auto",
			tableRef: {}
		};
	},
	methods: {
		// 取消选中单选
		setCurrent(row) {
			this.$refs.singleTable.setCurrentRow(row);
		},
		currentChange(page) {
			const current = {
				...this.pagination,
				page
			};

			this.$emit("current-change", page);
			this.$emit("update:pagination", current);
			this.$emit("paginationChange", current);

			this.scrollTop();
		},
		sizeChange(size) {
			const current = {
				...this.pagination,
				limit: size,
				page: 1
			};

			this.$emit("size-change", size);
			this.$emit("update:pagination", current);
			this.$emit("paginationChange", current);

			this.scrollTop();
		},
		/**
		 * 根据key返回对应的结果
		 * @param {String} key
		 * @param {Object} obj
		 * @param {Object} row
		 * @param {Number} index
		 */
		returnResultWithKey(key, obj, row, index) {
			if (!(key in obj)) {
				return false;
			}

			if (typeof obj[key] === "function") {
				return obj[key](row, index);
			}

			return !!obj[key];
		},
		/**
		 * 判断属性存在并且为函数
		 */
		attributeIsFunction(key, obj) {
			if (!(key in obj)) {
				return false;
			}

			return typeof obj[key] === "function";
		},
		doLayout() {
			this.$refs.tableRef && this.$refs.tableRef.doLayout();
		},
		scrollTop() {
			this.$nextTick(() => {
				this.$refs.tableRef && (this.$refs.tableRef.bodyWrapper.scrollTop = 0);
			});
		}
	},
	mounted() {
		this.$nextTick(() => {
			if (this.$refs.tableRef && this.$refs.tableRef.clearSelection) {
				this.clearSelection = this.$refs.tableRef.clearSelection;
			}
		});
	},
	beforeDestroy() {
		this.observer && this.observer.disconnect();
	},
	watch: {
		"$attrs.data": {
			handler() {
				if (this.flex) {
					const dom = this.$refs["xbd-table"];
					const showPagination =
						this.showPagination && this.pagination.total > (this.paginationConfig.pageSizes || [20, 50, 100])[0];

					if (dom) {
						this.maxHeight = dom.clientHeight - (showPagination ? 48 : 0);
					}
				}
			},
			deep: true
		}
	}
};
</script>

<style lang="scss" scoped>
.xbd-table {
	width: 100%;
	flex: 1 1 auto;
	display: flex;
	flex-direction: column;

	&__table {
		height: auto;

		.el-table {
			color: #31363f;
		}

		::v-deep .el-table__header {
			background-color: #f2f3f5;

			.el-table__cell {
				background-color: #f2f3f5;
				border-color: #e7e8eb !important;

				> .cell {
					color: #31363f;
					font-family: "PingFang SC";
					font-size: 12px;
					font-style: normal;
					font-weight: 600;
					line-height: 20px;
				}
			}
		}

		.operation {
			display: flex;
			justify-content: flex-start;
			align-items: center;
			flex-wrap: wrap;

			& > :not(:first-child) {
				margin-left: 12px !important;
			}

			::v-deep .el-button {
				font-weight: 400;
			}

			::v-deep .el-button--text {
				padding: 5px 0;
			}

			::v-deep .el-button + .el-button {
				margin-left: 0;
			}
		}
	}

	&__pagination {
		display: flex;
		justify-content: flex-end;
		margin-top: 16px;
	}
}
</style>
